import React, { useContext, useMemo } from 'react'
import { getValue } from 'firebase-utils'
import Policies from './steps/Policies'
import CampaignUsageStep from './steps/Usage'
import { ScreenSizeContext } from 'providers'
import { useLocation } from 'react-router-dom'
import CampaignContactStep from './steps/Contact'
import CampaignPreviewStep from './steps/Preview'
import CampaignPaymentStep from './steps/Payment'
import SampleMessages from './steps/SampleMessages'
import { getErrorMessage } from '../../utils/helpers'
import StepWizard from '../shared/StepWizardClone/WizardClone'
import BrandsContext from '../../contexts/BrandsContext'
import { WizardProps } from '../shared/types/WizardProps'
import PaymentFailedDialog from '../shared/PaymentFailedDialog'
import { CampaignContext } from '../../contexts/CampaignContext'

/**
 *
 */
const Wizard = ({
    item,
    onSubmit,
    goToListPage,
    ...props
}: WizardProps): JSX.Element => {
    // const [submitPhase, setSubmitPhase] = useState<'idle' | 'waiting' | 'pending'>('idle')

    const {
        initialFetchParams: initialBrandFetchParams,
        fetchBrands
    } = useContext(BrandsContext)

    const {
        initialFetchParams,
        fetchCampaigns,
        campaignNetworkError,
        setCampaignNetworkError
    } = useContext(CampaignContext)

    const screenSize = useContext(ScreenSizeContext)

    const location = useLocation()
    const goDireactlyToPaymentStep = useMemo(() => {
        const queryParams = new URLSearchParams(location.search)
        return item?.status === 'payment-failed' &&
            queryParams.has('payment-failed')
    }, [location.search, item?.status])

    const error = useMemo(() => campaignNetworkError ? getErrorMessage(campaignNetworkError) : null, [campaignNetworkError])

    const showPaymentFailedDialog = useMemo(() => {
        return (
            Array.isArray(campaignNetworkError) &&
            (
                campaignNetworkError?.some?.((x) => x?.description === 'payment-failed') ||
                campaignNetworkError?.some?.((x) => x?.description?.startsWith('Payment failed'))
            )
        )
    }, [campaignNetworkError])

    const onClosePaymentFailedDialog = () => {
        fetchBrands(initialBrandFetchParams)
        fetchCampaigns(initialFetchParams)
        setCampaignNetworkError(null)
        goToListPage?.()
    }

    const steps = useMemo(() => {
        const mode = item ? 'editing' : 'creating'

        return [
            {
                id: 'campaign_usage',
                label: getValue('sms_enrollment_campaigns_1st_step_title'),
                component: CampaignUsageStep,
                customBackButtonActionOnFirstStep: goToListPage,
                additionalProps: {
                    editing: item,
                    mode
                }
            },
            {
                id: 'campaign_sms_content',
                label: getValue('sms_enrollment_campaigns_2nd_step_title'),
                title: getValue('sms_enrollment_sample_message'),
                component: SampleMessages,
                additionalProps: {
                    editing: item,
                    mode
                }
            },
            {
                id: 'campaign_policies',
                label: getValue('sms_enrollment_campaigns_3rd_step_title'),
                title: getValue('sms_enrollment_content_and_messaging'),
                component: Policies,
                additionalProps: {
                    editing: item,
                    mode
                }
            },
            {
                id: 'campaign_contact',
                label: getValue('sms_enrollment_campaigns_4th_step_title'),
                title: getValue('sms_enrollment_contact_information'),
                component: CampaignContactStep,
                additionalProps: {
                    editing: item,
                    mode
                }
            },
            {
                id: 'campaign_preview',
                label: getValue('sms_enrollment_campaigns_5th_step_title'),
                component: CampaignPreviewStep,
                additionalProps: {
                    editing: item,
                    mode
                }
            },
            {
                id: 'campaign_invoice',
                label: getValue('sms_enrollment_campaigns_6th_step_title'),
                component: CampaignPaymentStep,
                additionalProps: {
                    showAlert: true,
                    showConfirmation: true,
                    editing: item,
                    mode
                    // onCancel: () => setShowWizard(false)
                }
            }
        ]
    }, [item])

    return <div>
        <StepWizard
            steps={steps}
            onSubmit={onSubmit}
            error={error}
            onCloseError={() => setCampaignNetworkError(null)}
            enableBackButtonOnFirstStep
            smallView={screenSize.mobile}
            initialActiveStep={goDireactlyToPaymentStep ? steps[steps.length - 1].id : undefined}
            {...props}
        />
        <PaymentFailedDialog
            isOpen={showPaymentFailedDialog}
            onClose={onClosePaymentFailedDialog}
        >
            {getValue('sms_registration_campaign_payment_failed_dialog_content')}
        </PaymentFailedDialog>
    </div>
}

export default Wizard
